import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { ReceiptText, Repeat, Check } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const OptionCard = ({ icon: Icon, title, desc, onClick, tid }) => (
  <button type="button" onClick={onClick} data-testid={tid}
    className="w-full text-left border border-slate-200 rounded-xl p-4 hover:border-navy hover:bg-slate-50 transition-colors flex gap-3">
    <Icon className="w-5 h-5 text-navy shrink-0 mt-0.5" />
    <div><p className="font-semibold text-navy">{title}</p><p className="text-xs text-slate-500 mt-0.5">{desc}</p></div>
  </button>
);

const Row = ({ k, v, tid }) => (
  <div className="flex justify-between gap-3 text-sm"><span className="text-slate-500">{k}</span><span className="font-medium text-navy text-right" data-testid={tid}>{v || "—"}</span></div>
);

function PartialForm({ inv, onDone, busy, setBusy }) {
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const ratio = inv.total_amount ? (inv.gst || 0) / inv.total_amount : 0.1;
  const ex = parseFloat(amt) || 0;
  const inc = Math.round(ex * (1 + ratio) * 100) / 100;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${inv.id}/credit-note/partial`, { amount_ex: ex, reason, notes });
      toast.success(`${r.data.credit_note_number} created & synced to Xero — outstanding now ${money(r.data.outstanding)}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="partial-credit-form">
      <div><Label className="text-xs">Credit amount (ex GST)</Label>
        <Input type="number" min="0" step="0.01" value={amt} onChange={(e) => setAmt(e.target.value)} className="mt-1" data-testid="partial-credit-amount" placeholder="0.00" /></div>
      <div className="rounded-lg bg-slate-50 p-3 space-y-1">
        <Row k="GST" v={money(inc - ex)} tid="partial-credit-gst" />
        <Row k="Credit total (inc GST)" v={`-${money(inc)}`} tid="partial-credit-inc" />
        <Row k="New outstanding" v={money(Math.max(0, (inv.outstanding || 0) - inc))} tid="partial-credit-new-outstanding" />
      </div>
      <div><Label className="text-xs">Reason for credit</Label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="partial-credit-reason" placeholder="Overcharge / scope adjustment / agreed credit" /></div>
      <div><Label className="text-xs">Notes (optional)</Label>
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="partial-credit-notes" /></div>
      <DialogFooter>
        <Button onClick={submit} disabled={busy || ex <= 0 || !reason.trim()} className="bg-navy" data-testid="partial-credit-confirm">
          {busy ? "Creating…" : "Create Credit Note & Sync to Xero"}
        </Button>
      </DialogFooter>
    </div>
  );
}

function RechargeForm({ inv, onDone, busy, setBusy }) {
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState("");
  const [reason, setReason] = useState("Invoice raised to incorrect customer.");
  const [updJob, setUpdJob] = useState(false);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers([...r.data].filter((c) => c.id !== inv.customer_id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
  }, [inv.customer_id]);
  const c = customers.find((x) => x.id === cid);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/invoices/${inv.id}/credit-note/recharge`, { customer_id: cid, reason, update_job_customer: updJob });
      toast.success(`${inv.invoice_number} credited (${r.data.credit_note_number}) — recharged as ${r.data.new_invoice_number} to ${r.data.recharge_customer}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="recharge-form">
      <div className="rounded-lg bg-slate-50 p-3 space-y-1">
        <Row k="Current Customer" v={inv.billing_company || inv.customer_name} tid="recharge-current-customer" />
        <Row k="Current Invoice" v={inv.invoice_number} tid="recharge-current-invoice" />
        <Row k="Current Amount" v={money(inv.total_inc_gst)} tid="recharge-current-amount" />
      </div>
      <div>
        <Label className="text-xs">Recharge To</Label>
        <Command className="border border-slate-200 rounded-lg mt-1">
          <CommandInput placeholder="Search existing Builder / Customer…" data-testid="recharge-customer-search" />
          <CommandList className="max-h-40">
            <CommandEmpty>No customer found.</CommandEmpty>
            {customers.map((x) => (
              <CommandItem key={x.id} value={`${x.name} ${x.id}`} onSelect={() => setCid(x.id)} data-testid={`recharge-customer-option-${x.id}`}>
                <Check className={`w-4 h-4 mr-2 ${cid === x.id ? "opacity-100" : "opacity-0"}`} />{x.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </div>
      {c && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-1" data-testid="recharge-new-customer-details">
          <Row k="New customer" v={c.billing_company || c.name} tid="recharge-new-name" />
          <Row k="Accounts email" v={c.accounts_email || c.billing_email || c.email} tid="recharge-new-email" />
          <Row k="Payment terms" v={c.payment_terms || "30 Days"} tid="recharge-new-terms" />
          <Row k="Billing contact" v={c.billing_contact} tid="recharge-new-contact" />
          <Row k="Xero contact" v={c.xero_contact_name || (c.xero_contact_id ? "Linked" : "Will be created/linked on sync")} tid="recharge-new-xero" />
        </div>
      )}
      <div><Label className="text-xs">Reason</Label>
        <Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="recharge-reason" /></div>
      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
        <input type="checkbox" checked={updJob} onChange={(e) => setUpdJob(e.target.checked)} data-testid="recharge-update-job-customer" />
        Update Job Customer / Builder to New Customer
      </label>
      <DialogFooter>
        <Button onClick={submit} disabled={busy || !cid || !reason.trim()} className="bg-red-600 hover:bg-red-700" data-testid="recharge-confirm">
          {busy ? "Processing…" : "Confirm Credit & Recharge"}
        </Button>
      </DialogFooter>
    </div>
  );
}

export const CreditRechargeDialog = ({ inv, onClose, onDone }) => {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(""); }, [inv]);
  const done = () => { onClose(); onDone(); };
  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="credit-recharge-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy">
            {mode === "partial" ? "Partial Credit Note" : mode === "recharge" ? "Full Credit & Recharge" : "Credit / Recharge"}
            {inv ? ` · ${inv.invoice_number}` : ""}
          </DialogTitle>
        </DialogHeader>
        {inv && !mode && (
          <div className="space-y-2">
            <OptionCard icon={ReceiptText} title="Partial Credit Note" tid="credit-option-partial" onClick={() => setMode("partial")}
              desc="Credit a specific amount off this invoice. The invoice and dockets stay with the same customer." />
            <OptionCard icon={Repeat} title="Full Credit & Recharge" tid="credit-option-recharge" onClick={() => setMode("recharge")}
              desc="Invoice went to the wrong customer: credit it in full and re-issue a new invoice to another customer." />
          </div>
        )}
        {inv && mode === "partial" && <PartialForm inv={inv} onDone={done} busy={busy} setBusy={setBusy} />}
        {inv && mode === "recharge" && <RechargeForm inv={inv} onDone={done} busy={busy} setBusy={setBusy} />}
        {mode && !busy && <button type="button" onClick={() => setMode("")} className="text-xs text-slate-500 hover:text-navy" data-testid="credit-back">← Back to options</button>}
      </DialogContent>
    </Dialog>
  );
};

// Per-credit-note PDF download + email (Awaiting Payment cards).
export const CreditNoteActions = ({ inv }) => {
  const [busy, setBusy] = useState("");
  const notes = inv.credit_notes || [];
  if (!notes.length) return null;
  const pdf = async (n) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/credit-notes/${n}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${n}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error("Could not download the credit note PDF"); }
  };
  const email = async (n) => {
    setBusy(n);
    try { const r = await api.post(`/invoices/${inv.id}/credit-notes/${n}/email`, {}); toast.success(r.data.message); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`cn-actions-${inv.id}`}>
      {notes.map((c) => (
        <div key={c.number} className="flex flex-wrap items-center gap-1.5 border border-red-100 bg-red-50/40 rounded-lg px-2 py-1 max-w-full">
          <span className="text-xs font-semibold text-red-700 whitespace-nowrap">{c.number}</span>
          <span className="text-xs text-slate-500">-{money(c.total_inc)}</span>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => pdf(c.number)} data-testid={`cn-pdf-${c.number}`}>Credit Note PDF</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={busy === c.number} onClick={() => email(c.number)} data-testid={`cn-email-${c.number}`}>
            {busy === c.number ? "Sending…" : c.emailed_at ? "Email Again" : "Email Credit Note"}
          </Button>
        </div>
      ))}
    </div>
  );
};
